import { Alert, Group, Progress, Select, Stack, Text } from '@mantine/core';
import { BookOpen, GraduationCap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Section } from '../ui';
import { useCohort, useCohortCourses } from '../../hooks/useCohorts';
import { useAssignCourseToCohort, useCourses } from '../../hooks/useCourses';

/**
 * Assign a whole course to a cohort — enrols every current member so it shows
 * under each student's Courses tab. Sits beside "Assign labs" on the cohort page.
 */
export function AssignCourse({ cohortId }: { cohortId: string }) {
  const cohort = useCohort(cohortId);
  const courses = useCourses();
  const cohortCourses = useCohortCourses(cohortId);
  const assign = useAssignCourseToCohort();
  const qc = useQueryClient();
  const [courseId, setCourseId] = useState<string | null>(null);

  const memberIds = useMemo(
    () => (cohort.data?.members ?? []).map((m) => m.user_id),
    [cohort.data],
  );
  const options = useMemo(
    () => (courses.data ?? []).map((c) => ({ value: c.id, label: c.title })),
    [courses.data],
  );

  const submit = async () => {
    const course = courses.data?.find((c) => c.id === courseId);
    if (!course || memberIds.length === 0) return;
    await assign.mutateAsync({ slug: course.slug, courseId: Number(course.id), userIds: memberIds });
    setCourseId(null);
    qc.invalidateQueries({ queryKey: ['cohorts'] });
  };

  const assigned = cohortCourses.data?.items ?? [];

  return (
    <Section
      title="Assign a course"
      description="Enrol everyone in this cohort into a course — it appears in each student's Courses tab."
    >
      <Card>
        <Stack gap="sm">
          <Select
            data={options}
            value={courseId}
            onChange={setCourseId}
            searchable
            clearable
            nothingFoundMessage="No courses"
            placeholder={courses.isPending ? 'Loading courses…' : 'Choose a course'}
            leftSection={<GraduationCap size={14} />}
            disabled={courses.isPending}
            aria-label="Choose a course to assign"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {memberIds.length} student{memberIds.length === 1 ? '' : 's'} will be enrolled.
            </Text>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<GraduationCap size={14} />}
              onClick={() => void submit()}
              loading={assign.isPending}
              disabled={!courseId || memberIds.length === 0}
            >
              Assign course
            </Button>
          </Group>
          {assign.data ? (
            <Alert color="green" variant="light">
              Enrolled {assign.data.enrolled_count} student{assign.data.enrolled_count === 1 ? '' : 's'}
              {typeof assign.data.newly_enrolled === 'number' ? ` (${assign.data.newly_enrolled} new)` : ''}.
            </Alert>
          ) : null}
          {assign.isError ? (
            <Alert color="red" variant="light">{(assign.error as Error).message}</Alert>
          ) : null}
        </Stack>
      </Card>

      <Card>
        <Stack gap="xs">
          <Group gap={6}>
            <BookOpen size={14} color="rgb(var(--c-neon))" />
            <Text fw={600} size="sm">Assigned courses</Text>
          </Group>
          {cohortCourses.isPending ? (
            <Text size="xs" c="dimmed">Loading…</Text>
          ) : assigned.length === 0 ? (
            <Text size="xs" c="dimmed">
              No courses assigned yet. Pick one above to enrol the whole cohort.
            </Text>
          ) : (
            assigned.map((c) => {
              const total = c.member_count || memberIds.length || 0;
              const pct = total > 0 ? Math.round((c.enrolled / total) * 100) : 0;
              return (
                <Stack key={c.id} gap={4} style={{ paddingBottom: 8 }}>
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm" c="rgb(var(--c-txt-bright))" lineClamp={1}>{c.name}</Text>
                    <Badge variant={c.enrolled >= total && total > 0 ? 'success' : 'info'} size="xs">
                      {c.enrolled}/{total} enrolled
                    </Badge>
                  </Group>
                  <Progress value={pct} size="xs" color="teal" radius="xl" />
                </Stack>
              );
            })
          )}
        </Stack>
      </Card>
    </Section>
  );
}
